import { useRef, useEffect } from 'react';
import type { RefObject } from 'react';

import { withScrimmedPortal } from '@skyscanner-internal/backpack-web/bpk-scrim-utils';
import StringsPropVerification from '@web-platform/global-components-higher-order-components/StringsPropVerification';

import { BannerActions } from './components/BannerActions';
import { BannerContent } from './components/BannerContent';
import { COMPONENT_NAME } from './constants';
import { consentBannerStringKeys } from './helpers';
import { useCMPSDK } from './hooks/useCMPSDK';
import { useConsentActions } from './hooks/useConsentActions';

import type { ConsentBannerProps } from './types';

import STYLES from './ConsentBanner.module.scss';

/** Props for the inner content component, including dialogRef injected by withScrimmedPortal. */
type ConsentBannerContentProps = ConsentBannerProps & {
  dialogRef?: RefObject<HTMLDivElement>;
};

/**
 * Internal content component for ConsentBanner.
 * This is wrapped with withScrimmedPortal to provide the scrim and portal.
 *
 * This component only mounts when shouldShow is true (controlled by parent),
 * so it always renders its content — no internal visibility check needed.
 */
const ConsentBannerContent = (props: ConsentBannerContentProps) => {
  const { dialogRef, logger, onPolicyAcceptClick, strings } = props;

  const bannerMessageRef = useRef<HTMLDivElement>(null);

  // Consent action handlers
  const { acceptAll, manageSettings, rejectAll } = useConsentActions({
    onComplete: () => {
      // Notify parent (consumer handles focus restoration)
      onPolicyAcceptClick(null);
    },
    logger,
  });

  // Auto-focus banner message on mount (same as CookieBanner)
  useEffect(() => {
    bannerMessageRef.current?.focus();
  }, []);

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-labelledby="consentBannerTitle"
      className={STYLES['consent-banner']}
      id="consentBannerContent"
    >
      <div className={STYLES['consent-banner__wrapper']}>
        <img
          className={STYLES['consent-banner__image']}
          alt=""
          width="80"
          height="100"
          src="https://content.skyscnr.com/m/67dcbbe580384179/original/Cookie-Promise.svg"
        />
        <div
          tabIndex={-1}
          ref={bannerMessageRef}
          className={STYLES['consent-banner__message']}
          data-testid="consentBannerMessage"
        >
          <BannerContent
            strings={strings}
            logger={logger}
            cookiePolicyUrl={props.cookiePolicyUrl}
          />
        </div>
        <BannerActions
          onAcceptAll={acceptAll}
          onRejectAll={rejectAll}
          onManageSettings={manageSettings}
          strings={strings}
        />
      </div>
    </div>
  );
};

/**
 * Wrapped component with scrim and portal.
 */
// @ts-expect-error — bpk-scrim-utils withScrimmedPortal type mismatch
const WrappedConsentBannerContent = withScrimmedPortal(ConsentBannerContent);

/**
 * ConsentBanner Component.
 *
 * @experimental This component is unstable and its API may change.
 *
 * A GDPR-compliant consent banner powered by Ketch CMP.
 * This component manages consent collection for users, allowing them to
 * accept all cookies, reject non-essential cookies, or manage their preferences.
 *
 * @example
 * ```tsx
 * <ConsentBanner
 *   isProductionEnv={process.env.NODE_ENV === 'production'}
 *   onPolicyAcceptClick={() => {
 *     dispatch(cookieBannerAcknowledged());
 *     focusElement?.focus();
 *   }}
 *   strings={{
 *     title: "We value your privacy",
 *     declineAllButtonAcceptAll: "Accept all",
 *     declineAllButtonManageSettings: "Manage settings",
 *     declineAllButtonAcceptEssentialOnly: "Accept essential only",
 *     // ... other required strings
 *   }}
 *   logger={{
 *     logError,
 *     logWarn,
 *     logOperationalEvent,
 *   }}
 * />
 * ```
 */
const ConsentBanner = (props: ConsentBannerProps) => {
  // Load SDK unconditionally — the SDK must always load so Ketch can decide
  // whether to show the banner via onShowConsentExperience/onHideConsentExperience callbacks.
  const { shouldShow } = useCMPSDK({
    logger: props.logger,
  });

  // On the scrim-disabled path we render `ConsentBannerContent` directly, so we must
  // supply the `dialogRef` ourselves (on the default path the `withScrimmedPortal` HOC
  // injects it).
  const dialogRef = useRef<HTMLDivElement>(null);

  // Don't mount the scrimmed portal until Ketch says to show.
  // This ensures both banner AND scrim appear/disappear together,
  // matching CookieBanner's behavior where the parent controls mounting.
  // eslint-disable-next-line no-underscore-dangle -- internal-only prop for Percy snapshots (not in ConsentBannerProps)
  if (!(props as unknown as Record<string, unknown>).__forceVisible && !shouldShow) {
    return null;
  }

  return (
    <StringsPropVerification
      componentName={COMPONENT_NAME}
      isProductionEnv={props.isProductionEnv}
      logger={props.logger}
      strings={props.strings}
      stringsKeys={consentBannerStringKeys}
    >
      {props.disableScrim ? (
        // No scrim/portal: render inline so the user can interact with (read/scroll)
        // the page beneath the banner — e.g. the Cookie Policy page. Kept in parity
        // with CookieBanner's disableScrim path.
        <ConsentBannerContent {...props} dialogRef={dialogRef} />
      ) : (
        <WrappedConsentBannerContent
          {...props}
          closeOnScrimClick={false}
          getApplicationElement={() => document.querySelector('#pagewrap')}
          renderTarget={() => document.getElementById('modal-container')}
          // The containerClassName is important - ad blockers may target cookie banners.
          // Without this wrapper, only part of the banner may be removed, leaving the scrim.
          containerClassName="consent-banner-wrapper"
        />
      )}
    </StringsPropVerification>
  );
};

export default ConsentBanner;
